import React from 'react'
import { arrayOf, number, shape, string, oneOf, object, bool } from 'prop-types'
import { ButtonGroup } from '@/component/Primitive/Events'
import styles from './TierOneSponsor.module.scss'
import ResponsiveImage from '@/component/Primitive/ResponsiveImage'
import cx from 'classnames'
import Container from '@/component/Primitive/Container'
import Hide from '@/component/Primitive/Hide'
import Type from '@/component/Primitive/Type'

export const TierOneSponsor = ({
  displayName,
  theme,
  tier,
  description,
  image,
  logo,
  inverse,
  fullUrlPath
}) => {
  const { mobile: mobileImage, desktop: desktopImage } = image
  const { mobile: mobileLogo, desktop: desktopLogo } = logo

  const buttonGroup = fullUrlPath
    ? [
        {
          type: 'link',
          variant: 'tertiary',
          label: 'Visit sponsor booth',
          link: `/${fullUrlPath}`
        }
      ]
    : []

  return (
    <div
      className={cx(
        styles.TierOneSponsor,
        theme === 'dark' && styles.dark,
        theme === 'grey' && styles.grey,
        inverse && styles.inverse
      )}
    >
      <Container className={styles.TierOneSponsorInner} size="wide" center>
        <Image device="desktop" image={desktopImage} className={styles.Cover} />
        <Image device="mobile" image={mobileImage} className={styles.Cover} />
        <Container gutter center className={styles.ContentWrapper}>
          <div className={styles.Content}>
            <div className={styles.Stack}>
              <Image
                device="mobile"
                image={mobileLogo}
                className={styles.Logo}
              />
              <div className={styles.Header}>
                <Image
                  device="desktop"
                  image={desktopLogo}
                  className={styles.Logo}
                />
                <div className={styles.HeaderText}>
                  <Type
                    as="h1"
                    size="body3"
                    weight="regular"
                    className={styles.Tier}
                  >
                    {tier.name}
                  </Type>
                  <Type
                    as="h1"
                    size={['heading4', 'heading3']}
                    weight="bold"
                    className={styles.DisplayName}
                  >
                    {displayName}
                  </Type>
                </div>
              </div>
              <Type
                className={styles.Description}
                size={['body3', 'body3', 'body2']}
              >
                <span dangerouslySetInnerHTML={{ __html: description }} />
              </Type>
              {!!buttonGroup?.length && (
                <ButtonGroup group={buttonGroup} theme={theme} />
              )}
            </div>
          </div>
        </Container>
      </Container>
    </div>
  )
}

TierOneSponsor.propTypes = {
  displayName: string,
  tier: string,
  description: string,
  theme: string,
  inverse: bool,
  fullUrlPath: string,
  image: shape({
    mobile: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    }),
    desktop: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    })
  }),
  logo: shape({
    mobile: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    }),
    desktop: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    })
  })
}

const Image = ({ device, image, className }) => {
  const hideProps =
    device === 'mobile' ? { at: 'desktop' } : { below: 'desktop' }
  const cn = device === 'mobile' ? styles.mobileImage : styles.desktopImage
  return (
    <Hide {...hideProps} className={cx(styles.ImageWrapper, className)}>
      <ResponsiveImage className={cx(styles.Image, cn)} {...image} />
    </Hide>
  )
}

Image.propTypes = {
  device: oneOf(['mobile', 'desktop']),
  image: object,
  className: string
}
